import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AUDIENCES = [
  "Students",
  "Freelancers",
  "Entrepreneurs",
  "Working Professionals",
  "Designers",
  "Marketers",
  "Developers",
];

function Stat({ value, label, suffix }) {
  return (
    <div
      data-testid={`stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="rounded-3xl border border-slate-200/80 dark:border-white/10 bg-white dark:bg-white/5 p-8 hairline"
    >
      <div className="font-display text-4xl sm:text-5xl font-semibold tracking-tighter text-[#0F172A] dark:text-white">
        {value}
        {suffix && (
          <span className="text-[#06B6D4] ml-0.5">{suffix}</span>
        )}
      </div>
      <div className="mt-2 text-sm text-slate-600 dark:text-slate-300">
        {label}
      </div>
    </div>
  );
}

export default function TrustedBy() {
  const [stats, setStats] = useState({
    students_enrolled: 12500,
    completion_rate: 92,
    success_stories: 1800,
    community_members: 25000,
  });

  useEffect(() => {
    axios
      .get(`${API}/stats`)
      .then((r) => setStats(r.data))
      .catch(() => {});
  }, []);

  return (
    <section
      data-testid="section-trusted-by"
      className="py-12 sm:py-16 border-y border-slate-200/70 dark:border-white/5"
    >
      <div className="max-w-7xl mx-auto px-6 sm:px-8 lg:px-12">
        <div className="flex flex-col items-center text-center">
          <span className="text-[11px] uppercase tracking-[0.24em] text-[#2563EB] dark:text-[#06B6D4] font-semibold">
            Trusted by ambitious learners
          </span>
          <h2 className="font-display text-3xl sm:text-4xl mt-3 font-semibold tracking-tight text-[#0F172A] dark:text-white">
            Built for the next generation of skill-builders.
          </h2>
        </div>

        <div className="mt-12 overflow-hidden">
          <div className="marquee flex gap-12 whitespace-nowrap text-slate-400 dark:text-slate-500 text-sm uppercase tracking-[0.2em]">
            {[...AUDIENCES, ...AUDIENCES, ...AUDIENCES].map((a, i) => (
              <span
                key={i}
                className="inline-flex items-center gap-3"
                data-testid={`audience-chip-${i}`}
              >
                <span className="h-1.5 w-1.5 rounded-full bg-[#06B6D4]" />
                {a}
              </span>
            ))}
          </div>
        </div>

        <motion.div
          initial="hidden"
          whileInView="show"
          viewport={{ once: true, margin: "-80px" }}
          variants={{
            hidden: {},
            show: { transition: { staggerChildren: 0.08 } },
          }}
          className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-5"
        >
          {[
            {
              v: `${(stats.students_enrolled / 1000).toFixed(1)}K`,
              s: "+",
              l: "Students Enrolled",
            },
            {
              v: `${(stats.community_members / 1000).toFixed(0)}K`,
              s: "+",
              l: "Community Members",
            },
            { v: "120", s: "K+", l: "Learning Hours" },
            { v: `${(stats.success_stories / 1000).toFixed(1)}K`, s: "+", l: "Success Stories" },
          ].map((s) => (
            <motion.div
              key={s.l}
              variants={{
                hidden: { opacity: 0, y: 20 },
                show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
              }}
            >
              <Stat value={s.v} suffix={s.s} label={s.l} />
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
